import React, {useEffect, useMemo, useRef} from 'react';
import {useHomeStore} from '../store';
import {AnchorTabContainer} from '@/components-v2';
import HomeDice from '@/pages/home/v2/components/home-games/home-floor-dice';
import {
  HomeGameCardItem,
  HomeGameFloorItem,
  IHomeSportsCardItem,
} from '@/pages/home/home.type';
import HomeColor from '@/pages/home/v2/components/home-games/home-floor-color';
import HomeDigits from '@/pages/home/v2/components/home-games/home-floor-3d';
import HomeCar from '@/pages/home/v2/components/home-games/home-floor-car';
import HomeKerala from '@/pages/home/v2/components/home-games/home-floor-kerala';
import HomeMatka from '@/pages/home/v2/components/home-games/home-floor-satta';
import HomeLottery from '@/pages/home/v2/components/home-games/home-floor-mix-lotto';
import HomeFloorPick11 from '@/pages/home/v2/components/home-games/home-floor-pick11';
import {sportsIconMap, toSportItem} from '@/pages/gr8-sports/converts';
import SportFloor from '@/pages/gr8-sports/components/sport-floor';
import globalStore from '@/services/global.state';

interface LotteryProps {
  scrollViewRef?: React.RefObject<any>;
  scrollY?: number;
}

export default function Lottery({scrollViewRef}: LotteryProps) {
  const {gameTypes, keralaList, matkaList, pick11List, getLotteryGames} =
    useHomeStore();
  const lotteryContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getLotteryGames();
  }, [getLotteryGames]);

  const handleSportLink = (floor: HomeGameFloorItem) => {
    if (floor.uniqueKey === 'HotMatches') {
      globalStore.openSports.next('/cricket');
      return;
    }
    if (floor.uniqueKey === 'TableTennis') {
      globalStore.openSports.next('/table-tennis/live');
      return;
    }
    if (floor.uniqueKey === 'CyberSport') {
      globalStore.openSports.next('/e-sports/live');
      return;
    }

    globalStore.openSports.next(`/${floor.uniqueKey.toLowerCase()}/live`);
  };

  const items = useMemo(() => {
    return gameTypes
      .map((item, i) => {
        const renderContent = () => {
          if (item.tagName?.endsWith('_sport') && item.gamesList?.length) {
            try {
              const gamesList = item.gamesList as IHomeSportsCardItem[];
              const sport = item.tagName.split('_')[0];
              const isSport =
                gamesList[0].grSportName.toLowerCase() === sport.toLowerCase();
              const isHotOrPremierLeague =
                item.uniqueKey === 'HotMatches' ||
                item.uniqueKey === 'PremierLeague' ||
                item.uniqueKey === 'Premier League';

              return (
                <SportFloor
                  icon={item.categoryPic}
                  title={item.name}
                  sportsList={gamesList.map(game => toSportItem(game))}
                  hasLink={isSport || isHotOrPremierLeague}
                  hasLiveIcon={isSport}
                  onLinkClick={() => handleSportLink(item)}
                  isSport={isSport}
                  defaultSportIcon={
                    item.categoryPic ||
                    sportsIconMap[sport as keyof typeof sportsIconMap] ||
                    undefined
                  }
                />
              );
            } catch {
              return null;
            }
          }

          switch (item.uniqueKey) {
            case 'dice':
              return (
                <HomeDice
                  id={`anchor-scroll-item-${i}`}
                  key={i}
                  name={item.name}
                  games={item.gamesList as HomeGameCardItem[]}
                  categoryPic={item.categoryPic}
                />
              );
            case 'color':
              return (
                <HomeColor
                  id={`anchor-scroll-item-${i}`}
                  key={i}
                  name={item.name}
                  serverTime={item.currentTimestamp || 0}
                  games={item.gamesList as HomeGameCardItem[]}
                  categoryPic={item.categoryPic}
                />
              );
            case '3Digits':
              return (
                <HomeDigits
                  id={`anchor-scroll-item-${i}`}
                  serverTime={item.currentTimestamp || 0}
                  key={i}
                  name={item.name}
                  categoryPic={item.categoryPic}
                />
              );
            case 'quickRace':
              return (
                <HomeCar
                  id={`anchor-scroll-item-${i}`}
                  serverTime={item.currentTimestamp || 0}
                  key={i}
                  name={item.name}
                  games={item.gamesList as HomeGameCardItem[]}
                  categoryPic={item.categoryPic}
                />
              );
            case 'kerala':
              return (
                <HomeKerala
                  keralaList={keralaList}
                  id={`anchor-scroll-item-${i}`}
                  key={i}
                  categoryPic={item.categoryPic}
                  name={item.name}
                  serverTime={item.currentTimestamp || 0}
                />
              );
            case 'matka':
              return (
                <HomeMatka
                  id={`anchor-scroll-item-${i}`}
                  key={i}
                  name={item.name}
                  categoryPic={item.categoryPic}
                  matkaList={matkaList}
                />
              );
            case 'indianLottery':
              return (
                <HomeLottery
                  id={`anchor-scroll-item-${i}`}
                  key={i}
                  name={item.name}
                  games={item.gamesList as HomeGameCardItem[]}
                  categoryPic={item.categoryPic}
                />
              );
            case 'Pick11':
            case 'pick11':
              if (!pick11List.length) {
                return null;
              }
              return (
                <HomeFloorPick11
                  id={`anchor-scroll-item-${i}`}
                  key={i}
                  name={item.name}
                  categoryPic={item.categoryPic}
                  icon={item.icon}
                  pick11List={pick11List}
                />
              );
            default:
              return null;
          }
        };

        const content = renderContent();

        if (!content) {
          return null;
        }

        return {
          key: item.uniqueKey,
          title: item.name,
          content,
        };
      })
      .filter(Boolean) as {
      key: string;
      title: string;
      content: React.ReactNode;
    }[];
  }, [gameTypes, keralaList, matkaList, pick11List]);

  return (
    <div className="bg-light-grey" ref={lotteryContainerRef}>
      {items.length ? (
        <AnchorTabContainer
          items={items}
          scrollContainer={scrollViewRef}
          className="anna-anchor-tabs"
          headerClassName="anna-anchor-tabs__header"
          tabListClassName="anna-anchor-tabs__tab-list"
          tabItemClassName="anna-anchor-tabs__tab-item"
          tabItemActiveClassName="anna-anchor-tabs__tab-item-active"
          indicatorClassName="anna-anchor-tabs__indicator"
          sectionClassName={key =>
            `anna-lottery-section anna-lottery-section--${key}`
          }
        />
      ) : null}
    </div>
  );
}
